import { Clear, Edit, Search } from '@mui/icons-material';
import { Box, IconButton, InputAdornment } from '@mui/material';
import { QueryFieldFilterConstraint, where } from 'firebase/firestore';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { ClientCafe } from '../../apiTypes';
import { Column } from '../../components/DataTable';
import DataTablePaginated from '../../components/DataTablePaginated';
import PrimaryButton from '../../components/PrimaryButton';
import StyledTextField from '../../components/styled/StyledTextField';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { usePagination } from '../../utils/api';
import useDebounce from '../../utils/useDebounce';

import EditClient from './EditClient';

type Row = DbDocument<ClientCafe>;

const ClientList = () => {
  const { cafeSlug } = useAdmin();
  const { worker } = useCafe();
  const { lang } = useLocale();
  const [client, setClient] = useState<DbDocument<ClientCafe> | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSeacrh] = useState('');

  const changeSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value === '+' || value === '') {
      setSeacrh(value);
    } else {
      setSeacrh(`+${value.replace(/[^0-9]/g, '')}`);
    }
  }, []);

  const whereQuery: QueryFieldFilterConstraint[] = useMemo(
    () =>
      search
        ? [where('type', '==', 'individual'), where('phoneNumber', '==', search)]
        : [where('type', '==', 'individual')],
    [search]
  );

  const whereQueryDebounced = useDebounce(whereQuery, 500);

  const {
    page,
    onPageChange,
    data: clients,
    isLoading,
    revalidate,
    count,
  } = usePagination<ClientCafe>({
    path: `cafes/${cafeSlug}/clients`,
    orderByField: 'added',
    where: whereQueryDebounced,
  });

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'name',
        title: t.userName[lang],
        align: 'left',
        render: (r: Row) => r.name,
      },
      {
        name: 'phoneNumber',
        title: t.phone[lang],
        align: 'left',
        render: (r: Row) => r.phoneNumber,
      },
      {
        name: 'edit',
        title: '',
        align: 'right',
        render: (r: Row) => (
          <IconButton
            onClick={() => {
              setClient(r);
              setIsOpen(true);
            }}
            disabled={!worker?.credentials.add_and_edit_clients}
          >
            <Edit />
          </IconButton>
        ),
      },
    ],
    [lang, worker?.credentials.add_and_edit_clients]
  );

  return (
    <>
      <DataTablePaginated
        columns={columns}
        rows={clients || []}
        page={page}
        count={count}
        onPageChange={onPageChange}
        loading={isLoading}
        customHeader={
          <Box width={230}>
            <StyledTextField
              size="small"
              value={search}
              label={t.phone[lang]}
              onChange={changeSearch}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSeacrh('')}>
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Box>
        }
        actionButton={
          <PrimaryButton
            onClick={() => setIsOpen(true)}
            variant="contained"
            disabled={!worker?.credentials.add_and_edit_clients}
          >
            {t.add_client[lang]}
          </PrimaryButton>
        }
        onRowClick={(r) => setClient(r)}
      />

      {isOpen && (
        <EditClient
          client={client}
          onClose={() => {
            setIsOpen(false);
            setClient(null);
          }}
          onSubmit={revalidate}
        />
      )}
    </>
  );
};

export default ClientList;
